import React, { useState, useEffect, useCallback, useRef } from 'react';
import { callKindLabel } from '../../lib/callKind';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import { AnalysisModal } from '../../components/AnalysisModal';
import { SmsActivity } from '../../components/agent/SmsActivity';
import { TypeToConfirmDialog } from '../../components/TypeToConfirmDialog';
import { VoiceEntry, ChatEntry, TimelineDateHeader, dateGroupLabel } from '../../components/contact-timeline/TimelineEntries';
import {
  Users, Search, Phone, PhoneIncoming, PhoneOutgoing, Play, Pause,
  Clock, CheckCircle, XCircle, X, Download, Calendar, Bot, User,
  ArrowUpRight, ArrowDownLeft, Volume2, MessageSquare, FileText,
  ChevronRight, ChevronDown, Copy, RefreshCw, TrendingUp, Percent, Timer,
  Activity, Pencil, Check, LayoutList, Trash2, Brain, Save,
  Edit3, Loader2, Briefcase, Target, AlertTriangle, Zap, Settings,
  Eye, EyeOff, Tag
} from 'lucide-react';

const API_URL = window.location.origin;

/* ═══════════ Masked Field (privacy toggle for phone / contact id) ═══════════
 * Shows the value partially redacted by default with an eyeball icon to
 * reveal it. Useful when the agency owner is recording a video walkthrough
 * and doesn't want to leak the contact's real phone or LeadConnector contact id.
 *
 * - mask: a function (val) => masked string. Defaults to "show first 3
 *   and last 2 chars" for short ids, or "(XXX) XXX-1234" style for phones.
 */
const MaskedField = ({ value, label, mask, testId, className = '' }) => {
  const [revealed, setRevealed] = React.useState(false);
  if (!value) return null;
  const display = revealed ? value : (mask ? mask(value) : defaultMask(value));
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid={testId}>
      {label && <span className="opacity-60">{label}</span>}
      <span className="font-mono">{display}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setRevealed(r => !r); }}
        className="opacity-50 hover:opacity-100 transition"
        aria-label={revealed ? 'Hide' : 'Show'}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {revealed ? <EyeOff size={11} /> : <Eye size={11} />}
      </button>
    </span>
  );
};

const defaultMask = (value) => {
  const s = String(value || '');
  if (!s) return '';
  // Looks-like-phone: keep area code + last 2 digits visible.
  // Format: "(917) •••-••30" — enough for the agency owner to
  // recognize the lead in a video walkthrough without leaking
  // the full number publicly.
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 7 && digits.length <= 15) {
    // Drop leading country code (1 for US, etc.) so we always
    // show a 3-digit area code from the actual local number.
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (local.length >= 7) {
      const area = local.slice(0, 3);
      const last2 = local.slice(-2);
      return `(${area}) •••-••${last2}`;
    }
    // Short / non-NANP: last 2 only
    return `•••-••${digits.slice(-2)}`;
  }
  // Email: keep first 2 chars of local part + the @domain visible
  // ("es•••@tecartemis.com") so the owner can recognize the lead.
  if (s.includes('@')) {
    const [local, domain] = s.split('@');
    if (local && domain) {
      const head = local.slice(0, 2);
      return `${head}${'•'.repeat(Math.max(3, local.length - 2))}@${domain}`;
    }
  }
  // Otherwise (e.g. LeadConnector contact_id): show first 4 + last 3.
  if (s.length <= 8) return s.slice(0, 2) + '•••' + s.slice(-2);
  return s.slice(0, 4) + '•••' + s.slice(-3);
};

/* ═══════════ Outcome Badge ═══════════ */
const OutcomeBadge = ({ outcome }) => {
  const config = {
    appointment_booked: { label: 'Booked', color: 'bg-green-400/10 text-green-400 border-green-400/20' },
    booked: { label: 'Booked', color: 'bg-green-400/10 text-green-400 border-green-400/20' },
    interested: { label: 'Interested', color: 'bg-blue-400/10 text-blue-400 border-blue-400/20' },
    not_interested: { label: 'Not Interested', color: 'bg-orange-400/10 text-orange-400 border-orange-400/20' },
    voicemail: { label: 'Voicemail', color: 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20' },
    callback_scheduled: { label: 'Callback', color: 'bg-blue-400/10 text-blue-400 border-blue-400/20' },
    lead_qualified: { label: 'Qualified', color: 'bg-violet-400/10 text-violet-400 border-violet-400/20' },
    issue_resolved: { label: 'Resolved', color: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' },
    transferred: { label: 'Transferred', color: 'bg-amber-400/10 text-amber-400 border-amber-400/20' },
    no_answer: { label: 'No Answer', color: 'bg-red-400/10 text-red-400 border-red-400/20' },
    no_action: { label: 'No Action', color: 'bg-gray-400/10 text-gray-400 border-gray-400/20' },
  };
  const c = config[outcome] || config.no_action;
  return <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium border ${c.color}`} data-testid={`outcome-${outcome}`}>{c.label}</span>;
};

/* ═══════════ Contact Detail Panel ═══════════ */
const ContactDetailPanel = ({ phone, subAccountId, onClose, isDarkMode, onNameUpdated }) => {
  const { api } = useAuth();
  const intelVersionRef = useRef(0);
  const intelIdentityRef = useRef({ contact_id: null, memory_revision: null });
  const profileKeyRef = useRef('');
  profileKeyRef.current = `${subAccountId}:${phone}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [intel, setIntel] = useState(null);
  const [intelExpanded, setIntelExpanded] = useState(false);
  const [editingIntel, setEditingIntel] = useState(false);
  const [intelForm, setIntelForm] = useState({ notes: '', business_name: '', key_interests: '', pain_points: '', sentiment: '' });
  const [savingIntel, setSavingIntel] = useState(false);
  const [analyzingContact, setAnalyzingContact] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [agents, setAgents] = useState([]);

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';

  useEffect(() => {
    intelVersionRef.current += 1;
    setIntel(null);
    setGeneratingIntel(false);
    setIntelForm({ notes: '', business_name: '', key_interests: '', pain_points: '', sentiment: '' });
    if (phone) { fetchTimeline(); fetchIntel(); fetchAgents(); }
    return () => { intelVersionRef.current += 1; };
  }, [phone, subAccountId]);

  const fetchTimeline = async () => {
    const profileKey = profileKeyRef.current;
    setLoading(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}/timeline`);
      if (profileKey !== profileKeyRef.current) return;
      setData(res.data);
      setNewName(res.data?.contact_name || '');
    } catch { toast.error('Failed to load contact timeline'); }
    finally { setLoading(false); }
  };

  const [generatingIntel, setGeneratingIntel] = useState(false);

  const fetchIntel = async () => {
    const version = ++intelVersionRef.current;
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}/intelligence`);
      if (version !== intelVersionRef.current) return;
      intelIdentityRef.current = { contact_id: res.data?.contact_id ?? null, memory_revision: res.data?.memory_revision ?? null };
      setIntel(res.data);
      setIntelForm({
        notes: res.data?.notes || '',
        business_name: res.data?.business_name || '',
        key_interests: res.data?.key_interests || '',
        pain_points: res.data?.pain_points || '',
        sentiment: res.data?.sentiment || '',
      });
      // Auto-generate intelligence if there are interactions but no manual data
      const hasInteractions = res.data?.auto_data?.total_interactions > 0;
      const hasIntel = res.data?.business_name || res.data?.key_interests || res.data?.pain_points || res.data?.notes;
      if (hasInteractions && !hasIntel) {
        generateIntel();
      }
    } catch { /* intelligence is optional */ }
  };

  const generateIntel = async () => {
    const version = intelVersionRef.current;
    const identity = { ...intelIdentityRef.current };
    setGeneratingIntel(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}/intelligence/generate`);
      if (version !== intelVersionRef.current) return;
      if ((res.data?.contact_id !== undefined && res.data.contact_id !== identity.contact_id) ||
          (res.data?.memory_revision !== undefined && res.data.memory_revision !== identity.memory_revision)) {
        await fetchIntel();
        return;
      }
      if (res.data) {
        setIntel(prev => ({ ...prev, ...res.data }));
        setIntelForm(prev => ({
          ...prev,
          notes: res.data?.notes || '',
          business_name: res.data?.business_name || '',
          key_interests: res.data?.key_interests || '',
          pain_points: res.data?.pain_points || '',
          sentiment: res.data?.sentiment || '',
        }));
        toast.success('Intelligence generated');
      }
    } catch (err) {
      if (version !== intelVersionRef.current || err?.response?.status === 409) return;
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Analysis failed: ${detail}`);
      console.error('Intel generation error:', detail);
    }
    finally { if (version === intelVersionRef.current) setGeneratingIntel(false); }
  };

  const fetchAgents = async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/agents`);
      setAgents(res.data || []);
    } catch { /* non-critical */ }
  };

  const saveIntel = async () => {
    setSavingIntel(true);
    try {
      const res = await api.put(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}/intelligence`, intelForm);
      setIntel(prev => ({ ...prev, ...res.data }));
      setEditingIntel(false);
      toast.success('Contact intelligence updated');
    } catch { toast.error('Failed to save'); }
    setSavingIntel(false);
  };

  const saveName = async () => {
    if (!newName.trim()) return;
    try {
      await api.post(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}/update-name`, { name: newName.trim() });
      toast.success('Contact name updated');
      setEditingName(false);
      if (onNameUpdated) onNameUpdated();
      fetchTimeline();
    } catch { toast.error('Failed to update name'); }
  };

  const [deletingContact, setDeletingContact] = useState(false);

  const deleteContact = async () => {
    setDeletingContact(true);
    try {
      const resp = await api.delete(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}`);
      // Build an itemized confirmation toast so the operator sees
      // exactly what got wiped — critical for the "test fresh" flow
      // where you need to be sure nothing lingered behind.
      const d = resp?.data?.deleted || {};
      const items = [];
      if (d.calls) items.push(`${d.calls} call${d.calls > 1 ? 's' : ''}`);
      if (d.chat_sessions) items.push(`${d.chat_sessions} chat${d.chat_sessions > 1 ? 's' : ''}`);
      if (d.orb_sessions) items.push(`${d.orb_sessions} orb session${d.orb_sessions > 1 ? 's' : ''}`);
      toast.success(items.length ? `Cleared ${items.join(', ')}` : 'Contact deleted');
      setConfirmDelete(null);
      if (onNameUpdated) onNameUpdated();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Failed to delete: ${detail}`);
      console.error('Delete contact error:', err?.response?.status, detail);
    } finally {
      setDeletingContact(false);
    }
  };

  const deleteTimelineEntry = async (entryId, entryType) => {
    intelVersionRef.current += 1;
    setIntel(null);
    setGeneratingIntel(false);
    setIntelForm({ notes: '', business_name: '', key_interests: '', pain_points: '', sentiment: '' });
    try {
      // Optimistic removal — immediately remove entry from local state
      setData(prev => {
        if (!prev?.timeline) return prev;
        return {
          ...prev,
          timeline: prev.timeline.filter(e => {
            const eid = ['call', 'voice'].includes(entryType) ? e.call_id : e.session_id;
            return eid !== entryId;
          }),
          stats: {
            ...prev.stats,
            total_interactions: Math.max(0, (prev.stats?.total_interactions || 0) - 1),
            total_calls: entryType === 'call' ? Math.max(0, (prev.stats?.total_calls || 0) - 1) : (prev.stats?.total_calls || 0),
            total_chats: (entryType === 'chat' || entryType === 'chat-widget')
              ? Math.max(0, (prev.stats?.total_chats || 0) - 1)
              : (prev.stats?.total_chats || 0),
          }
        };
      });
      setConfirmDelete(null);
      // Backend timeline-delete endpoint expects "chat" for both SMS
      // and widget chat sessions — collapse the type before the API call.
      const apiEntryType = (entryType === 'chat-widget') ? 'chat' : entryType;
      await api.delete(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}/timeline/${entryId}?entry_type=${apiEntryType}`);
      toast.success('Entry deleted');
      await Promise.all([fetchTimeline(), fetchIntel()]);
      if (onNameUpdated) onNameUpdated();
    } catch {
      toast.error('Failed to delete entry');
      await Promise.all([fetchTimeline(), fetchIntel()]);
      fetchTimeline(); // Revert on error
    }
  };

  const fmtDuration = (s) => !s ? '0:00' : `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className={`w-full md:w-[72vw] md:max-w-[1500px] md:min-w-[920px] ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} border-l ${cardBorder} h-full overflow-y-auto`}
        onClick={e => e.stopPropagation()} data-testid="contact-detail-panel">
        {loading ? (
          <div className="flex items-center justify-center h-full"><div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" /></div>
        ) : data ? (
          <>
            {/* Header */}
            <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} border-b ${cardBorder} p-5`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center text-white font-bold text-lg">
                    {(data.contact_name || data.phone_number)?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    {editingName ? (
                      <div className="flex items-center gap-2">
                        <input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveName()}
                          className={`text-lg font-semibold ${textPrimary} ${inputBg} border ${cardBorder} rounded-lg px-2 py-0.5 w-48 focus:outline-none focus:border-teal-400`}
                          autoFocus data-testid="edit-name-input" />
                        <button onClick={saveName} className="p-1 rounded bg-teal-400/20 text-teal-400" data-testid="save-name-btn"><Check size={14} /></button>
                        <button onClick={() => setEditingName(false)} className={`p-1 rounded bg-white/10 ${textSecondary}`}><X size={14} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h3 className={`text-lg font-semibold ${textPrimary}`} data-testid="contact-name">{data.contact_name || 'Unknown Contact'}</h3>
                        <button onClick={() => { setEditingName(true); setNewName(data.contact_name || ''); }} className={`p-1 rounded-lg ${textSecondary} hover:text-teal-400 hover:bg-white/10`} data-testid="edit-name-btn"><Pencil size={12} /></button>
                      </div>
                    )}
                    <div className="mt-0.5">
                      <MaskedField
                        value={data.phone_number}
                        testId="contact-phone"
                        className={`text-sm ${textSecondary}`}
                      />
                    </div>
                    {data.contact_email && (
                      <div className="mt-0.5">
                        <MaskedField
                          value={data.contact_email}
                          label=""
                          testId="contact-email"
                          className={`text-xs ${textSecondary}`}
                        />
                      </div>
                    )}
                    {data.contact_id && (
                      <div className="mt-0.5">
                        <MaskedField
                          value={data.contact_id}
                          label="LeadConnector:"
                          testId="contact-ghl-id"
                          className={`text-xs ${textSecondary} opacity-80`}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <button onClick={onClose} className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`} data-testid="close-contact-panel"><X size={18} /></button>
              </div>

              {/* Actions */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button onClick={() => setShowAnalysisModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg text-violet-400 hover:bg-violet-400/10 border border-transparent hover:border-violet-400/20 transition"
                  data-testid="analyze-contact-btn">
                  <Brain size={12} /> Analyze
                </button>
                <button onClick={async () => {
                  setSyncing(true);
                  try {
                    await api.post(`/api/sub-accounts/${subAccountId}/sync-call-transcripts`);
                    toast.success('Transcripts synced');
                    fetchTimeline();
                  } catch { toast.error('Sync failed'); }
                  setSyncing(false);
                }} disabled={syncing} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg text-teal-400 hover:bg-teal-400/10 border border-transparent hover:border-teal-400/20 disabled:opacity-50`} data-testid="sync-transcripts-btn">
                  <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing...' : 'Sync'}
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => setConfirmDelete('contact')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg text-red-400 hover:bg-red-400/10 border border-transparent hover:border-red-400/20`}
                  data-testid="delete-contact-btn"
                >
                  <Trash2 size={12} /> Delete Contact
                </button>
              </div>

              {/* Typed-confirmation gate — wipes EVERY entity tied to
                  this contact (calls, chats, orb sessions, recordings,
                  analytics, scheduled dials, callbacks). Operator must
                  type DELETE to enable. */}
              <TypeToConfirmDialog
                open={confirmDelete === 'contact'}
                onCancel={() => setConfirmDelete(null)}
                onConfirm={deleteContact}
                title="Delete this contact?"
                description="Wipes ALL data for this contact: voice calls, chat sessions, orb conversations, recordings, analytics, scheduled callbacks. This is the full reset. Cannot be undone."
                destructiveLabel="Delete contact"
                isDarkMode={isDarkMode}
                busy={deletingContact}
                testIdPrefix="delete-contact"
              />

              {/* Per-contact analytics removed — dashboard is the single
                  source of truth for metrics */}
            </div>

            {/* Intelligence Card */}
            {intel && (
              <div className="px-5 pt-4" data-testid="contact-intelligence-card">
                <button onClick={() => setIntelExpanded(!intelExpanded)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border ${cardBorder} ${isDarkMode ? 'bg-gradient-to-r from-violet-500/5 to-teal-500/5 hover:from-violet-500/10 hover:to-teal-500/10' : 'bg-violet-50 hover:bg-violet-100'} transition`}
                  data-testid="toggle-intel-card">
                  <div className="flex items-center gap-2">
                    <Brain size={15} className="text-violet-400" />
                    <span className={`text-sm font-medium ${textPrimary}`}>Contact Insights</span>
                    {(intel.auto_data?.total_interactions || 0) > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-white/[0.05] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        {intel.auto_data.total_interactions} interactions
                      </span>
                    )}
                  </div>
                  <ChevronRight size={14} className={`${textSecondary} transition-transform ${intelExpanded ? 'rotate-90' : ''}`} />
                </button>

                <AnimatePresence>
                  {intelExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden">
                      <div className={`mt-2 rounded-xl border ${cardBorder} ${isDarkMode ? 'bg-white/[0.02]' : 'bg-white'} p-4`}>
                        {!editingIntel ? (
                          <>
                            {generatingIntel && (
                              <div className={`flex items-center gap-2 mb-3 p-2.5 rounded-lg ${isDarkMode ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-200'}`}>
                                <Loader2 size={12} className="animate-spin text-violet-400" />
                                <span className="text-xs text-violet-400">Generating insights from conversations...</span>
                              </div>
                            )}
                            {/* Auto data — Last Contact / Avg Duration /
                                Appointments / Callbacks were removed per
                                user request. The "Pending callbacks"
                                upcoming-card below is still rendered when
                                there ARE callbacks queued, since that's
                                actionable info (vs. a static "0 pending"
                                stat). */}

                            {/* Pending callbacks */}
                            {intel.auto_data?.pending_callbacks?.length > 0 && (
                              <div className={`mb-3 p-2.5 rounded-lg ${isDarkMode ? 'bg-amber-400/5 border border-amber-400/10' : 'bg-amber-50 border border-amber-200'}`}>
                                <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1">Upcoming</p>
                                {intel.auto_data.pending_callbacks.map((cb, i) => (
                                  <p key={i} className={`text-xs ${textPrimary}`}>
                                    {cb.scheduled_at?.substring(0, 16).replace('T', ' at ')} — {cb.reason || 'Callback'}
                                  </p>
                                ))}
                              </div>
                            )}

                            {/* Manual fields */}
                            {[
                              { label: 'Business', value: intel.business_name, icon: Briefcase },
                              { label: 'Key Interests', value: intel.key_interests, icon: Target },
                              { label: 'Pain Points', value: intel.pain_points, icon: AlertTriangle },
                              { label: 'Notes', value: intel.notes, icon: FileText },
                            ].filter(f => f.value).map(f => (
                              <div key={f.label} className="flex items-start gap-2 mb-2">
                                <f.icon size={12} className="text-teal-400 mt-0.5 shrink-0" />
                                <div>
                                  <p className={`text-[10px] uppercase tracking-wider ${textSecondary}`}>{f.label}</p>
                                  <p className={`text-xs ${textPrimary}`}>{f.value}</p>
                                </div>
                              </div>
                            ))}

                            <div className="mt-2 flex items-center gap-3">
                              <button onClick={() => setEditingIntel(true)}
                                className="flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 transition"
                                data-testid="edit-intel-btn">
                                <Edit3 size={11} /> Edit
                              </button>
                              <button onClick={generateIntel} disabled={generatingIntel}
                                className="flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 transition disabled:opacity-50"
                                data-testid="refresh-intel-btn">
                                {generatingIntel ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                                {generatingIntel ? 'Analyzing...' : 'Refresh'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="space-y-3">
                            {[
                              { key: 'business_name', label: 'Business Name', placeholder: 'e.g., Roofing Pros LLC' },
                              { key: 'key_interests', label: 'Key Interests', placeholder: 'e.g., AI scheduling, automation' },
                              { key: 'pain_points', label: 'Pain Points', placeholder: 'e.g., Missed calls, slow follow-up' },
                              { key: 'sentiment', label: 'Sentiment', placeholder: 'e.g., Positive, Interested, Skeptical' },
                              { key: 'notes', label: 'Notes', placeholder: 'Additional notes about this contact...' },
                            ].map(f => (
                              <div key={f.key}>
                                <label className={`text-[10px] uppercase tracking-wider ${textSecondary} mb-1 block`}>{f.label}</label>
                                {f.key === 'notes' ? (
                                  <textarea value={intelForm[f.key]} onChange={e => setIntelForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    rows={2} placeholder={f.placeholder}
                                    className={`w-full rounded-lg border ${cardBorder} ${inputBg} ${textPrimary} p-2 text-xs focus:outline-none focus:border-teal-400/50 resize-none`}
                                    data-testid={`intel-${f.key}`} />
                                ) : (
                                  <input value={intelForm[f.key]} onChange={e => setIntelForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    placeholder={f.placeholder}
                                    className={`w-full rounded-lg border ${cardBorder} ${inputBg} ${textPrimary} p-2 text-xs focus:outline-none focus:border-teal-400/50`}
                                    data-testid={`intel-${f.key}`} />
                                )}
                              </div>
                            ))}
                            <div className="flex items-center gap-2">
                              <button onClick={saveIntel} disabled={savingIntel}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500 text-white text-xs font-medium hover:bg-teal-600 disabled:opacity-50 transition"
                                data-testid="save-intel-btn">
                                {savingIntel ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                              </button>
                              <button onClick={() => setEditingIntel(false)}
                                className={`px-3 py-1.5 rounded-lg text-xs ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Timeline */}
            <div className="p-5">
              <h4 className={`text-xs font-medium ${textSecondary} uppercase tracking-wide mb-4`}>
                All Interactions ({data.stats.total_interactions || data.timeline?.length || 0})
              </h4>
              <div className="relative" data-testid="contact-timeline-list">
                <div className="absolute left-[17px] top-2 bottom-2 w-[2px] rounded-full bg-gradient-to-b from-teal-500/50 via-indigo-500/30 to-violet-500/10 dark:from-teal-400/40 dark:via-indigo-400/20 dark:to-transparent" />
                <div className="space-y-2.5">
                  <AnimatePresence mode="popLayout">
                  {(data.timeline || []).map((entry, idx) => {
                    const isChat = entry.type === 'chat' || entry.type === 'chat-widget';
                    const ts = isChat ? (entry.last_message_at || entry.timestamp || entry.created_at) : entry.created_at;
                    const label = dateGroupLabel(ts);
                    const prev = data.timeline[idx - 1];
                    const prevLabel = prev ? dateGroupLabel((prev.type === 'chat' || prev.type === 'chat-widget') ? (prev.last_message_at || prev.timestamp || prev.created_at) : prev.created_at) : null;
                    const common = {
                      entry, idx, isExpanded: expandedEntry === idx,
                      onToggle: () => setExpandedEntry(expandedEntry === idx ? null : idx),
                      confirmDelete, setConfirmDelete,
                    };
                    return (
                    <motion.div key={entry.call_id || entry.session_id || idx}
                      layout
                      initial={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0, marginBottom: 0, overflow: 'hidden' }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}>
                      {label !== prevLabel && <TimelineDateHeader label={label} />}
                      {isChat ? (
                        <ChatEntry {...common} onDelete={() => deleteTimelineEntry(entry.session_id, entry.type)} />
                      ) : (
                        <VoiceEntry {...common} api={api} subAccountId={subAccountId} onDelete={() => deleteTimelineEntry(entry.call_id, 'voice')} />
                      )}
                    </motion.div>
                    );
                  })}
                  </AnimatePresence>
                </div>
              </div>
              {(!data.timeline || data.timeline.length === 0) && (
                <div className={`text-center py-12 ${inputBg} rounded-xl border ${cardBorder}`}>
                  <Phone className={`mx-auto mb-2 ${textSecondary}`} size={28} />
                  <p className={`text-sm ${textSecondary}`}>No interactions yet</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full"><p className={textSecondary}>Failed to load</p></div>
        )}
      </motion.div>

      {/* Analysis Modal */}
      <AnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        api={api}
        subAccountId={subAccountId}
        phone={phone}
        agents={agents}
        isDark={isDarkMode}
      />
    </motion.div>
  );
};

/* ═══════════ Recent Activity Entry ═══════════
 * Expandable row that exposes a full diagnostic panel on click:
 *   - IDs (call_id, twilio_sid, agent_id, sub_account_id, el_conversation_id)
 *   - Timeline (created → prewarm → dial → answered → completed)
 *   - Status + outcome + prewarm mode
 *   - Error / failure reason (only for failed calls)
 *   - "Copy diagnostic JSON" button that fetches the full diagnostic
 *     endpoint and copies a single blob to the clipboard so the user
 *     can paste it to support in one shot.
 *
 * Only voice/call rows expand for now — chat sessions keep the original
 * compact row.
 */
const RecentActivityRow = ({ entry, isDarkMode, onClick, api, subAccountId, selected, onToggleSelect, onDelete }) => {
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const panelBg = isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50/80';
  const isVoice = entry.type === 'voice';
  const phone = isVoice ? entry.phone_number : entry.contact_phone;
  const name = entry.contact_name || phone || 'Unknown';
  const fmtDate = (d) => { if (!d) return ''; const diff = Date.now() - new Date(d).getTime(); if (diff < 3600000) return 'Just now'; if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); };
  const fmtAbs = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString(); } catch { return d; } };
  const fmtDur = (s) => !s ? '-' : `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const [expanded, setExpanded] = React.useState(false);
  const [copying, setCopying] = React.useState(false);

  const canExpand = isVoice;

  const handleRowClick = (e) => {
    // Clicking the chevron / expandable region opens the diagnostic panel.
    // Clicking the contact name area still routes to the contact detail
    // page via the outer `onClick` handler.
    if (e.target.closest('[data-row-action]') === null && canExpand) {
      setExpanded(v => !v);
      return;
    }
    if (onClick) onClick();
  };

  const copyDiagnostic = async (e) => {
    e.stopPropagation();
    if (!entry.call_id) { toast.error('No call_id on this row'); return; }
    setCopying(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/calls/${entry.call_id}/diagnostic`);
      const blob = JSON.stringify(res.data, null, 2);
      try {
        await navigator.clipboard.writeText(blob);
        toast.success('Diagnostic copied — paste to support');
      } catch {
        // Clipboard API can be blocked in some embed contexts. Fall back to
        // a hidden textarea + execCommand so the copy still works.
        const ta = document.createElement('textarea');
        ta.value = blob;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Diagnostic copied — paste to support');
      }
    } catch (err) {
      toast.error(`Copy failed: ${err?.response?.data?.detail || err.message || 'unknown error'}`);
    } finally {
      setCopying(false);
    }
  };

  const statusColor = (s) => {
    if (s === 'completed') return 'bg-green-400/10 text-green-400';
    if (s === 'active') return 'bg-green-400/10 text-green-400';
    if (s === 'failed') return 'bg-red-400/10 text-red-400';
    if (s === 'no-answer' || s === 'busy' || s === 'canceled') return 'bg-amber-400/10 text-amber-400';
    if (s === 'warming' || s === 'queued' || s === 'initiated' || s === 'ringing') return 'bg-teal-400/10 text-teal-400';
    return 'bg-gray-400/10 text-gray-400';
  };

  return (
    <>
      <tr className={`border-b ${cardBorder} hover:bg-white/[0.03] transition-colors cursor-pointer ${selected ? (isDarkMode ? 'bg-teal-400/[0.05]' : 'bg-teal-50') : ''}`} onClick={handleRowClick} data-testid={`recent-row-${entry.call_id || entry.session_id}`}>
        <td className="px-3 py-3" data-row-action onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-400 focus:ring-teal-400/50 cursor-pointer"
            data-testid={`recent-row-select-${entry.call_id || entry.session_id}`}
          />
        </td>
        <td className="px-5 py-3">
          <div className="flex items-center gap-2.5">
            {canExpand ? (
              <button data-row-action onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }} className={`p-0.5 rounded ${textSecondary} hover:text-teal-400`} data-testid={`recent-row-expand-${entry.call_id}`}>
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : <span className="w-[18px]" />}
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isVoice ? (entry.direction === 'inbound' ? 'bg-teal-400/10' : 'bg-violet-400/10') : 'bg-blue-400/10'}`}>
              {isVoice ? (entry.direction === 'inbound' ? <ArrowDownLeft size={14} className="text-teal-400" /> : <ArrowUpRight size={14} className="text-violet-400" />) : <MessageSquare size={14} className="text-blue-400" />}
            </div>
            <div>
              <p className={`text-sm font-medium ${textPrimary}`}>{name}</p>
              <p className={`text-[11px] ${textSecondary} font-mono`}>{phone}</p>
            </div>
          </div>
        </td>
        <td className="px-5 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-md font-medium whitespace-nowrap ${isVoice ? 'bg-teal-400/10 text-teal-400' : 'bg-blue-400/10 text-blue-400'}`}>
            {isVoice ? callKindLabel(entry, true) : 'Chat'}
          </span>
          {isVoice && entry.ivr_routed_to && <div className={`text-[10px] mt-1 whitespace-nowrap ${textSecondary}`} data-testid="recent-ivr-routed">Routed to {entry.ivr_routed_to}</div>}
        </td>
        <td className="px-5 py-3">
          <span className={`text-xs px-1.5 py-0.5 rounded ${statusColor(entry.status)}`}>{entry.status}</span>
          <SmsActivity entry={entry} compact testId={`recent-sms-${entry.call_id || entry.session_id}`} />
        </td>
        <td className="px-5 py-3"><span className={`text-sm font-mono ${textPrimary}`}>{isVoice ? fmtDur(entry.duration) : `${entry.message_count || 0} msgs`}</span></td>
        <td className="px-5 py-3">{(() => {
          if (!entry.outcome) return <span className={textSecondary}>-</span>;
          const isBookedOutcome = entry.outcome === 'appointment_booked' || entry.outcome === 'booked';
          const isVoiceEntry = entry.type === 'voice';
          const requiredTag = isVoiceEntry ? 'ai voice booked' : 'ai chat booked';
          const hasBookedTag = (entry.tags_applied || []).map(t => String(t).toLowerCase()).includes(requiredTag);
          if (isBookedOutcome && !hasBookedTag) return <span className={textSecondary}>-</span>;
          return <OutcomeBadge outcome={entry.outcome} />;
        })()}</td>
        <td className="px-5 py-3"><span className={`text-xs ${textSecondary}`}>{entry.agent_name || '-'}</span></td>
        <td className="px-5 py-3"><span className={`text-sm ${textSecondary}`}>{fmtDate(entry.timestamp)}</span></td>
        <td className="px-3 py-3 text-right" data-row-action onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onDelete}
            title="Delete this activity"
            className={`p-1.5 rounded-md ${textSecondary} hover:text-red-400 hover:bg-red-400/10 transition-colors`}
            data-testid={`recent-row-delete-${entry.call_id || entry.session_id}`}
          >
            <Trash2 size={14} />
          </button>
        </td>
      </tr>
      {expanded && canExpand && (
        <tr className={`border-b ${cardBorder}`} data-testid={`recent-row-diag-${entry.call_id}`}>
          <td colSpan={9} className={`px-5 py-4 ${panelBg}`}>
            <DiagnosticPanel
              entry={entry}
              isDarkMode={isDarkMode}
              fmtAbs={fmtAbs}
              onCopy={copyDiagnostic}
              copying={copying}
            />
          </td>
        </tr>
      )}
    </>
  );
};

const DIAG_FAILURE_EXPLANATIONS = {
  el_attach_failed: 'The callee answered but the AI audio bridge died within ~1 second — the pre-warmed ElevenLabs session was already dead or closed instantly (EL concurrency/quota limit, EL closed mid-ring, or backend restart). New calls auto-salvage at pickup via the legacy answer-time flow.',
  warm_session_missing: 'The callee answered but no pre-warmed AI session existed in server memory (backend restart mid-ring, or the session was swept). New calls auto-salvage at pickup.',
  bridge_lookup_miss: 'Twilio delivered the pickup webhook to a server with no record of this call — almost always a WEBHOOK_BASE_URL / host mismatch between environments.',
  dial_failed: 'Twilio rejected or could not place the outbound call (number format, caller ID, or Twilio account status).',
  watchdog_no_dial: 'The dial was never placed within the expected window (background dial task died or backend restarted).',
};

/* ═══════════ Diagnostic Panel ═══════════
 * Rendered inside an expanded RecentActivityRow. Pulls every available
 * field off the call doc payload. The "Copy diagnostic JSON" button
 * hits the dedicated /diagnostic endpoint for the full (redacted) blob.
 */
const DiagnosticPanel = ({ entry, isDarkMode, fmtAbs, onCopy, copying }) => {
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const labelCls = `text-[10px] uppercase tracking-wider ${textMuted}`;
  const valCls = `text-xs font-mono ${textPrimary} break-all`;
  const headerCls = `text-[11px] font-semibold uppercase tracking-wider ${textSecondary} mb-2`;
  const cardCls = isDarkMode ? 'bg-black/30 border-white/[0.06]' : 'bg-white border-gray-200';

  const KV = ({ label, value, testId, mono = true }) => (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
      <span className={labelCls}>{label}</span>
      <span className={mono ? valCls : `text-xs ${textPrimary}`}>{value === undefined || value === null || value === '' ? '—' : String(value)}</span>
    </div>
  );

  const timing = entry.timing || {};
  const isFailed = entry.status === 'failed';

  const twilioConsoleUrl = entry.twilio_sid
    ? `https://console.twilio.com/us1/monitor/logs/calls?frameUrl=%2Fconsole%2Fvoice%2Fcalls%2Flogs%2F${entry.twilio_sid}`
    : null;

  return (
    <div className="recent-activity-details space-y-4">
      <SmsActivity entry={entry} testId={`recent-sms-detail-${entry.call_id}`} />
      {entry.summary && <p className={`text-sm leading-relaxed ${textPrimary}`} data-testid={`recent-summary-${entry.call_id}`}>{entry.summary}</p>}
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button
          onClick={onCopy}
          disabled={copying}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-400/10 text-teal-400 text-xs font-medium hover:bg-teal-400/20 transition disabled:opacity-50"
          data-testid={`diag-copy-${entry.call_id}`}
        >
          {copying ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />}
          {copying ? 'Copying…' : 'Copy diagnostic JSON'}
        </button>
        {twilioConsoleUrl && (
          <a
            href={twilioConsoleUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-xs font-medium hover:bg-white/10 transition"
            data-testid={`diag-twilio-${entry.call_id}`}
          >
            <Phone size={12} /> View in Twilio
          </a>
        )}
      </div>

      {/* IDs */}
      <details className="recent-technical" data-testid={`recent-technical-${entry.call_id || entry.session_id}`}>
      <summary className={`text-xs cursor-pointer py-2 ${textSecondary}`} data-testid={`recent-technical-toggle-${entry.call_id || entry.session_id}`}>Technical details & identifiers</summary>
      <div className="space-y-4 pt-3">
      <div className={`rounded-xl border ${cardCls} p-4`}>
        <div className={headerCls}>Identifiers</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <KV label="call_id" value={entry.call_id} testId={`diag-call-id-${entry.call_id}`} />
          <KV label="twilio_sid" value={entry.twilio_sid || '(never placed)'} testId={`diag-twilio-sid-${entry.call_id}`} />
          <KV label="agent_id" value={entry.agent_id} />
          <KV label="sub_account_id" value={entry.sub_account_id} />
          <KV label="el_conversation_id" value={entry.el_conversation_id} />
          <KV label="contact_id" value={entry.ghl_contact_id || entry.contact_id || (entry.context || {}).contact_id} />
          {(() => {
            // Direction-aware labels: `phone_number` always stores the
            // OTHER party's number (caller for inbound, contact for
            // outbound). Avoid misleading users into thinking we wrote
            // the wrong value to `to_number`.
            const isInbound = (entry.direction || '').toLowerCase() === 'inbound';
            const fromVal = isInbound ? entry.phone_number : (entry.to_number || entry.from_number);
            const toVal   = isInbound ? (entry.to_number || '') : entry.phone_number;
            return (
              <>
                <KV label="from_number" value={fromVal} />
                <KV label="to_number" value={toVal} />
              </>
            );
          })()}
        </div>
      </div>

      {/* Timeline */}
      <div className={`rounded-xl border ${cardCls} p-4`}>
        <div className={headerCls}>Timeline</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <KV label="created_at" value={fmtAbs(entry.created_at)} mono={false} />
          <KV label="prewarm_started_at" value={fmtAbs(timing.prewarm_started_at || entry.prewarm_started_at)} mono={false} />
          <KV label="dial_placed_at" value={fmtAbs(timing.dial_placed_at || entry.dial_placed_at)} mono={false} />
          <KV label="answered_at" value={fmtAbs(timing.answered_at || entry.answered_at)} mono={false} />
          <KV label="completed_at" value={fmtAbs(timing.completed_at || entry.completed_at || entry.ended_at)} mono={false} />
          <KV label="error_at" value={fmtAbs(entry.error_at)} mono={false} />
        </div>
      </div>

      {/* Status + prewarm */}
      <div className={`rounded-xl border ${cardCls} p-4`}>
        <div className={headerCls}>Status & Prewarm</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KV label="status" value={entry.status} />
          <KV label="outcome" value={entry.outcome} />
          <KV label="direction" value={entry.direction} />
          <KV label="source" value={entry.source} />
          <KV label="prewarm_mode" value={entry.prewarm_mode} />
          <KV label="prewarm_delay_s" value={entry.prewarm_delay_seconds} />
          <KV label="convai_open_error" value={entry.convai_open_error || '(none)'} />
          <KV label="telephony_provider" value={entry.telephony_provider} />
        </div>
      </div>

      {/* Error (only if failed) */}
      </div>
      </details>
      {isFailed && (
        <div className={`rounded-xl border border-red-400/30 ${isDarkMode ? 'bg-red-500/[0.04]' : 'bg-red-50'} p-4`}>
          <div className={`text-[11px] font-semibold uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1.5`}>
            <AlertTriangle size={12} /> Failure reason
          </div>
          {(entry.failure_reason || entry.prewarm_death_reason || entry.bridge_salvage) && (
            <div className="mb-4 space-y-3" data-testid={`diag-failure-block-${entry.call_id}`}>
              {(DIAG_FAILURE_EXPLANATIONS[entry.failure_reason] || entry.failure_reason) && (
                <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-red-300' : 'text-red-700'}`} data-testid={`diag-failure-explained-${entry.call_id}`}>
                  {DIAG_FAILURE_EXPLANATIONS[entry.failure_reason] || entry.failure_reason}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <KV label="failure_reason" value={entry.failure_reason} />
                <KV label="failure_stage" value={entry.failure_stage} />
                {entry.failure_detail && (
                  <div className="md:col-span-2"><KV label="failure_detail" value={entry.failure_detail} /></div>
                )}
                <KV label="which_side_ended_first" value={entry.failure_end_cause} />
                <KV label="ai_session_age_at_failure" value={entry.failure_session_age_s != null ? `${entry.failure_session_age_s}s` : undefined} />
                {entry.failure_el_pipe_detail && (
                  <div className="md:col-span-2"><KV label="elevenlabs_pipe" value={entry.failure_el_pipe_detail} /></div>
                )}
                {entry.failure_twilio_pipe_detail && (
                  <div className="md:col-span-2"><KV label="twilio_pipe" value={entry.failure_twilio_pipe_detail} /></div>
                )}
                {entry.prewarm_death_reason && (
                  <div className="md:col-span-2">
                    <KV label="ai_session_died_while_ringing" value={`${entry.prewarm_death_reason}${entry.prewarm_died_at ? ` @ ${fmtAbs(entry.prewarm_died_at)}` : ''}`} />
                  </div>
                )}
                {entry.bridge_salvage && <KV label="bridge_salvage" value={entry.bridge_salvage} />}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <KV label="error_type" value={entry.error_type || '(not captured)'} />
            <KV label="error_code" value={entry.error_code} />
            <KV label="error_step" value={entry.error_step} />
            <KV label="error_uri" value={entry.error_uri} />
            <div className="md:col-span-2">
              <KV label="error_message" value={entry.error_message || entry.error || '(no message)'} />
            </div>
            {entry.error_more_info && (
              <div className="md:col-span-2">
                <KV label="error_more_info" value={entry.error_more_info} />
              </div>
            )}
            {entry.error_traceback && (
              <div className="md:col-span-2">
                <span className={labelCls}>traceback</span>
                <pre className={`mt-1 text-[10px] font-mono ${textSecondary} max-h-40 overflow-auto whitespace-pre-wrap break-all p-2 rounded ${isDarkMode ? 'bg-black/40' : 'bg-gray-100'}`}>
                  {entry.error_traceback}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status callbacks received */}
      {Array.isArray(entry.status_callbacks) && entry.status_callbacks.length > 0 && (
        <div className={`rounded-xl border ${cardCls} p-4`}>
          <div className={headerCls}>Twilio Status Callbacks Received ({entry.status_callbacks.length})</div>
          <div className="space-y-1 max-h-40 overflow-auto">
            {entry.status_callbacks.map((cb, i) => (
              <div key={i} className="text-[11px] font-mono flex items-center gap-3">
                <span className={textMuted}>{fmtAbs(cb.received_at)}</span>
                <span className={textPrimary}>{cb.CallStatus || cb.status || JSON.stringify(cb).slice(0, 60)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════ Main Page Content ═══════════ */
const ContactTimelineContent = () => {
  const { workspaceId, subAccountId } = useParams();
  const { api } = useAuth();
  const { isDarkMode } = useTheme();

  const [tab, setTab] = useState('contacts'); // 'contacts' | 'recent'
  const [contacts, setContacts] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [showAnalyzeAll, setShowAnalyzeAll] = useState(false);
  const [agents, setAgents] = useState([]);

  // Recent Activity bulk selection
  const [selectedActivity, setSelectedActivity] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const cardBg = isDarkMode ? 'bg-[#0c0d12]/90' : 'bg-white/90';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';

  const fetchContacts = useCallback(async () => {
    // Contacts are a CORE memory surface — never blank them, never show
    // an error page. If the API hiccups we keep whatever's on screen and
    // retry silently in the background forever. Auth failures route to
    // the axios interceptor which already handles session refresh/logout.
    //
    // Spinner policy: only show on the *very first* mount fetch. Every
    // subsequent fetch (manual refresh, websocket-triggered refetch,
    // backoff retry) is silent — otherwise the page "blinks" as the
    // spinner flashes in and out on each background refresh.
    if (!fetchContacts._everLoaded) setLoading(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/contacts`);
      const incoming = Array.isArray(res.data) ? res.data : [];
      // Defensive: a 200 OK with an empty array when we already have
      // contacts on screen almost always means a transient upstream
      // hiccup (e.g. LeadConnector rate-limit returning [] without an HTTP error).
      // Don't wipe — keep the last-known list and retry silently. The
      // ONLY way to clear the list is through an explicit user action
      // or a fresh page load with a genuinely empty response.
      if (incoming.length === 0 && (fetchContacts._lastKnownCount || 0) > 0) {
        console.warn('[Contacts] received empty payload — keeping existing list, scheduling silent retry');
        setLoading(false);
        const attempt = (fetchContacts._emptyAttempts || 0) + 1;
        fetchContacts._emptyAttempts = attempt;
        const delay = Math.min(30000, 2000 * Math.pow(1.6, Math.min(attempt, 5)));
        setTimeout(() => { fetchContacts(); }, delay);
        return;
      }
      // Real data (or initial empty load) — accept it. Reset the empty-
      // retry counter so future hiccups get fresh backoff treatment.
      fetchContacts._emptyAttempts = 0;
      fetchContacts._lastKnownCount = incoming.length;
      fetchContacts._everLoaded = true;
      setContacts(incoming);
      setLoading(false);
      return;
    } catch (err) {
      console.warn('[Contacts] fetch failed — keeping existing list and will retry:', err?.response?.status || err?.message);
      // We deliberately DO NOT set an error state, DO NOT toast, and DO
      // NOT wipe `contacts`. Let the user keep working on whatever's
      // already loaded. Background retry with exponential backoff up to
      // 30s — authentication errors are handled by the axios interceptor.
      setLoading(false);
      fetchContacts._everLoaded = true; // never show spinner again
      const status = err?.response?.status;
      if (status === 401 || status === 403) return; // auth interceptor owns this
      const attempt = (fetchContacts._attempts || 0) + 1;
      fetchContacts._attempts = attempt;
      const delay = Math.min(30000, 1500 * Math.pow(1.6, Math.min(attempt, 6)));
      setTimeout(() => { fetchContacts(); }, delay);
    }
  }, [subAccountId, api]);

  const fetchRecent = useCallback(async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/recent-activity?limit=50`);
      const incoming = Array.isArray(res.data) ? res.data : [];
      // Same defensive pattern: an empty payload when we already had
      // activity rows is a transient hiccup, not a wipe signal.
      if (incoming.length === 0 && (fetchRecent._lastKnownCount || 0) > 0) return;
      fetchRecent._lastKnownCount = incoming.length;
      setRecentActivity(incoming);
    } catch { /* keep last-known list, do not wipe */ }
  }, [subAccountId, api]);

  useEffect(() => { fetchContacts(); fetchRecent(); fetchAgentsForPage(); }, [fetchContacts, fetchRecent]);

  // ─────────── Recent Activity row delete helpers ───────────
  const entryKey = (e) => e.call_id || e.session_id;
  const entryType = (e) => (e.type === 'chat' ? 'chat' : 'voice');

  const toggleSelectActivity = (e) => {
    const k = entryKey(e);
    if (!k) return;
    setSelectedActivity(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const toggleSelectAllActivity = (visibleRows) => {
    setSelectedActivity(prev => {
      const allSelected = visibleRows.every(r => prev.has(entryKey(r)));
      if (allSelected) return new Set();
      const next = new Set(prev);
      visibleRows.forEach(r => { const k = entryKey(r); if (k) next.add(k); });
      return next;
    });
  };

  const deleteSingleActivity = async (e) => {
    const k = entryKey(e);
    if (!k) return;
    if (!window.confirm('Delete this activity row? The contact stays — only this interaction is removed.')) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/recent-activity/${k}`, { params: { entry_type: entryType(e) } });
      toast.success('Activity deleted');
      setSelectedActivity(prev => { const n = new Set(prev); n.delete(k); return n; });
      fetchRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete activity');
    }
  };

  const performBulkDelete = async () => {
    if (selectedActivity.size === 0) return;
    setBulkDeleting(true);
    try {
      const entries = recentActivity
        .filter(e => selectedActivity.has(entryKey(e)))
        .map(e => ({ id: entryKey(e), type: entryType(e) }));
      const res = await api.post(`/api/sub-accounts/${subAccountId}/recent-activity/bulk-delete`, { entries });
      toast.success(`Deleted ${res.data?.deleted ?? entries.length} activit${(res.data?.deleted ?? 1) === 1 ? 'y' : 'ies'}`);
      setSelectedActivity(new Set());
      setConfirmBulkDelete(false);
      fetchRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const fetchAgentsForPage = async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/agents`);
      setAgents(res.data || []);
    } catch { /* non-critical */ }
  };

  const fmtDate = (d) => { if (!d) return '-'; const diff = Date.now() - new Date(d).getTime(); const hrs = Math.floor(diff / 3600000); if (hrs < 1) return 'Just now'; if (hrs < 24) return `${hrs}h ago`; const days = Math.floor(diff / 86400000); if (days < 7) return `${days}d ago`; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
  const fmtDur = (s) => !s ? '0:00' : `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const filtered = contacts.filter(c => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return c.phone_number?.includes(q) || c.contact_name?.toLowerCase().includes(q); });

  if (loading) return <div className={`min-h-screen ${bgMain} flex items-center justify-center`}><div className="w-12 h-12 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" /></div>;

  return (
    <div className={`min-h-screen ${bgMain} p-6 lg:p-8`} data-testid="contact-timeline-page">
      {isDarkMode && <div className="fixed inset-0 pointer-events-none overflow-hidden"><div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-teal-400/5 rounded-full blur-[100px]" /></div>}
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-semibold ${textPrimary}`}>Contact Timeline</h1>
            <p className={textSecondary}>All interactions across voice and chat channels</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAnalyzeAll(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-500/80 to-teal-500/80 text-white text-sm font-medium hover:opacity-90 transition"
              data-testid="analyze-all-contacts-btn">
              <Brain size={15} /> Analyze All
            </button>
            <button onClick={() => { fetchContacts(); fetchRecent(); }} className={`p-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textSecondary} hover:bg-white/10`} data-testid="refresh-contacts-btn"><RefreshCw size={18} /></button>
          </div>
        </div>

        {/* Page-level analytics removed — dashboard is the single source of truth */}

        {/* Tabs */}
        <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4 mb-6`}>
          <div className="flex items-center gap-4 mb-4">
            {[
              { id: 'contacts', label: 'Contacts', icon: Users },
              { id: 'recent', label: 'Recent Activity', icon: LayoutList },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' : `${textSecondary} hover:bg-white/5`}`} data-testid={`tab-${t.id}`}>
                <t.icon size={15} /> {t.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
            <input type="text" placeholder={tab === 'contacts' ? 'Search contacts...' : 'Search recent activity...'}
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-9 pr-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
              data-testid="ct-search-input" />
          </div>
        </div>

        {/* Tab Content */}
        {tab === 'contacts' ? (
          filtered.length > 0 ? (
            <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full block xl:table [&_td]:min-w-0" data-testid="contact-timeline-table">
                  <thead className="hidden xl:table-header-group">
                    <tr className={`border-b ${cardBorder} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                      {['Contact', 'Contact ID', 'Calls', 'Answered', 'Booked', 'Chats', 'Last Contact', ''].map(h => (
                        <th key={h} className={`px-5 py-3 text-left text-[11px] font-medium ${textSecondary} uppercase tracking-wider`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="block xl:table-row-group">
                    {filtered.map((contact, idx) => {
                      const totalCalls = Number(contact.total_calls);
                      const answeredCalls = Number(contact.answered_calls);
                      const answerRate = Number.isFinite(totalCalls) && totalCalls > 0
                        ? Math.round((Number.isFinite(answeredCalls) ? answeredCalls : 0) / totalCalls * 100) : 0;
                      return (
                        <tr key={contact.phone_number} className={`grid grid-cols-2 xl:table-row border-b ${cardBorder} hover:bg-white/[0.03] transition-colors cursor-pointer`}
                          onClick={() => setSelectedPhone(contact.phone_number)} data-testid={`contact-row-${idx}`}>
                          <td className="col-span-2 px-5 py-3.5">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-teal-400/20 to-violet-400/20 flex items-center justify-center text-sm font-semibold text-teal-400">{(contact.contact_name || contact.phone_number)?.[0]?.toUpperCase() || '?'}</div>
                              <div className="min-w-0">
                                <p className={`text-sm font-medium [overflow-wrap:anywhere] ${textPrimary}`}>{contact.contact_name || 'Unknown'}</p>
                                <MaskedField
                                  value={contact.phone_number}
                                  testId={`contact-row-${idx}-phone`}
                                  className={`text-xs ${textSecondary}`}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="block xl:hidden text-[10px] text-gray-400 mb-1">Contact ID</span>
                            {contact.contact_id ? (
                              <MaskedField
                                value={contact.contact_id}
                                testId={`contact-row-${idx}-id`}
                                className={`text-xs ${textSecondary}`}
                              />
                            ) : (
                              <span className={`text-xs ${textSecondary} opacity-50`}>—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="block xl:hidden text-[10px] text-gray-400 mb-1">Calls</span>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-medium ${textPrimary}`}>{contact.total_calls}</span>
                              <div className="flex items-center gap-1">
                                {contact.inbound_calls > 0 && <span className="text-[10px] text-teal-400 flex items-center gap-0.5"><ArrowDownLeft size={9} />{contact.inbound_calls}</span>}
                                {contact.outbound_calls > 0 && <span className="text-[10px] text-violet-400 flex items-center gap-0.5"><ArrowUpRight size={9} />{contact.outbound_calls}</span>}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="block xl:hidden text-[10px] text-gray-400 mb-1">Answered</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-green-400 font-medium">{contact.answered_calls}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${answerRate >= 70 ? 'bg-green-400/10 text-green-400' : answerRate >= 40 ? 'bg-yellow-400/10 text-yellow-400' : 'bg-red-400/10 text-red-400'}`}>{answerRate}%</span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5"><span className="block xl:hidden text-[10px] text-gray-400 mb-1">Booked</span><span className={`text-sm ${contact.appointments_booked > 0 ? 'text-green-400 font-medium' : textSecondary}`}>{contact.appointments_booked}</span></td>
                          <td className="px-5 py-3.5"><span className="block xl:hidden text-[10px] text-gray-400 mb-1">Chats</span><span className={`text-sm ${contact.chat_sessions > 0 ? 'text-teal-400 font-medium' : textSecondary}`}>{contact.chat_sessions || 0}</span></td>
                          <td className="px-5 py-3.5"><span className="block xl:hidden text-[10px] text-gray-400 mb-1">Last Contact</span><span className={`text-sm ${textSecondary}`}>{fmtDate(contact.last_call_at)}</span></td>
                          <td className="px-5 py-3.5"><ChevronRight size={16} className={textSecondary} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            // Two states only — loading or genuinely empty. Error UI is
            // intentionally absent: contacts must NEVER disappear. If
            // the API hiccups, `fetchContacts` retries silently and we
            // keep displaying whatever we have (which is the previous
            // list, or this "Loading…" placeholder on first load).
            loading ? (
              <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-16 text-center`} data-testid="contacts-loading-state">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400/10 to-violet-400/10 flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="text-teal-400 animate-spin" size={28} />
                </div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>Loading contacts…</h3>
              </div>
            ) : (
              <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-16 text-center`} data-testid="contacts-empty-state">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400/10 to-violet-400/10 flex items-center justify-center mx-auto mb-4"><Users className="text-gray-600" size={32} /></div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>No Contacts Yet</h3>
                <p className={`${textSecondary} text-sm max-w-md mx-auto`}>Contacts will appear here once your AI agents start making and receiving calls.</p>
              </div>
            )
          )
        ) : (
          /* Recent Activity Tab */
          recentActivity.length > 0 ? (
            <>
              {/* Bulk-delete toolbar */}
              {selectedActivity.size > 0 && (
                <div className={`mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-red-400/30 bg-red-400/[0.06]`} data-testid="recent-bulk-toolbar">
                  <div className="text-sm">
                    <span className="text-white font-medium">{selectedActivity.size}</span>
                    <span className={textSecondary}> activit{selectedActivity.size === 1 ? 'y' : 'ies'} selected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedActivity(new Set())}
                      className={`px-3 py-1.5 text-xs rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10`}
                      data-testid="recent-bulk-clear"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmBulkDelete(true)}
                      className="px-3 py-1.5 text-xs rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium flex items-center gap-1.5"
                      data-testid="recent-bulk-delete"
                    >
                      <Trash2 size={12} /> Delete Selected
                    </button>
                  </div>
                </div>
              )}

              <div className={`recent-activity-surface ${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} overflow-hidden`} data-testid="recent-activity-surface">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${cardBorder} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                      <th className="px-3 py-3 text-left w-10">
                        {(() => {
                          const visible = recentActivity.filter(e => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return (e.phone_number || e.contact_phone || '').includes(q) || (e.contact_name || '').toLowerCase().includes(q); });
                          const allSelected = visible.length > 0 && visible.every(r => selectedActivity.has(entryKey(r)));
                          return (
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() => toggleSelectAllActivity(visible)}
                              className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-400 focus:ring-teal-400/50 cursor-pointer"
                              title={allSelected ? 'Deselect all' : 'Select all'}
                              data-testid="recent-select-all"
                            />
                          );
                        })()}
                      </th>
                      {['Contact', 'Channel', 'Status', 'Duration', 'Outcome', 'Agent', 'Time'].map(h => (
                        <th key={h} className={`px-5 py-3 text-left text-[11px] font-medium ${textSecondary} uppercase tracking-wider`}>{h}</th>
                      ))}
                      <th className="px-3 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.filter(e => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return (e.phone_number || e.contact_phone || '').includes(q) || (e.contact_name || '').toLowerCase().includes(q); }).map((entry) => (
                      <RecentActivityRow key={entry.call_id || entry.session_id} entry={entry} isDarkMode={isDarkMode}
                        api={api} subAccountId={subAccountId}
                        selected={selectedActivity.has(entryKey(entry))}
                        onToggleSelect={() => toggleSelectActivity(entry)}
                        onDelete={() => deleteSingleActivity(entry)}
                        onClick={() => setSelectedPhone(entry.phone_number || entry.contact_phone)} />
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            </>
          ) : (
            <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-16 text-center`}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400/10 to-violet-400/10 flex items-center justify-center mx-auto mb-4"><Activity className="text-gray-600" size={32} /></div>
              <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>No Recent Activity</h3>
              <p className={`${textSecondary} text-sm max-w-md mx-auto`}>Recent calls and chat sessions will appear here.</p>
            </div>
          )
        )}
      </div>

      <AnimatePresence>
        {selectedPhone && (
          <ContactDetailPanel key={`${subAccountId}:${selectedPhone}`} phone={selectedPhone} subAccountId={subAccountId} onClose={() => setSelectedPhone(null)}
            isDarkMode={isDarkMode} onNameUpdated={fetchContacts} />
        )}
      </AnimatePresence>

      {/* Analyze All Modal */}
      <AnalysisModal
        isOpen={showAnalyzeAll}
        onClose={() => setShowAnalyzeAll(false)}
        api={api}
        subAccountId={subAccountId}
        phone={null}
        agents={agents}
        isDark={isDarkMode}
      />

      {/* Bulk delete confirmation modal */}
      <AnimatePresence>
        {confirmBulkDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
            onClick={() => !bulkDeleting && setConfirmBulkDelete(false)}
            data-testid="bulk-delete-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className={`w-full max-w-md ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${cardBorder} flex items-center gap-3`}>
                <div className="w-11 h-11 rounded-xl bg-red-400/15 border border-red-400/30 flex items-center justify-center">
                  <Trash2 className="text-red-400" size={20} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Delete {selectedActivity.size} activit{selectedActivity.size === 1 ? 'y' : 'ies'}?</h3>
                  <p className={`text-sm ${textSecondary}`}>This action cannot be undone.</p>
                </div>
              </div>
              <div className="p-6 space-y-3">
                <p className={`text-sm ${textPrimary}`}>
                  You're about to permanently delete <span className="font-semibold">{selectedActivity.size}</span> activity row{selectedActivity.size === 1 ? '' : 's'}.
                </p>
                <p className={`text-xs ${textSecondary}`}>
                  The contacts themselves stay intact — only these specific call/chat records will be removed. If the same contact interacts again, they'll reappear with a fresh activity entry.
                </p>
              </div>
              <div className={`p-6 border-t ${cardBorder} flex gap-3`}>
                <button
                  onClick={() => setConfirmBulkDelete(false)}
                  disabled={bulkDeleting}
                  className={`flex-1 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors disabled:opacity-50`}
                  data-testid="bulk-delete-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={performBulkDelete}
                  disabled={bulkDeleting}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500 hover:bg-red-600 disabled:bg-red-500/40 text-white font-medium transition-colors"
                  data-testid="bulk-delete-confirm"
                >
                  {bulkDeleting ? 'Deleting…' : <><Trash2 size={16} /> Delete {selectedActivity.size}</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ContactTimelinePage = () => (
  <SubAccountLayout>
    <ContactTimelineContent />
  </SubAccountLayout>
);

export default ContactTimelinePage;
